import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { InputNumber, Input, DatePicker, Button, message, Form, Modal, Spin } from 'antd';
import {
	SaveOutlined,
	CloseOutlined,
	EditOutlined,
	EyeOutlined,
	PaperClipOutlined,
	FileTextOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import styled from 'styled-components';
import { Box, Text } from 'atoms';
import TdsFileUpload from './TdsFileUpload';
import GstInvoiceUploadModal from './GstInvoiceUploadModal';
import VehicleReplacementHistoryModal from './VehicleReplacementHistoryModal';
import { updateVendorBillWithApproval } from 'store/vendorBillApprovalSlice';
import { getSignedUrl } from 'store/commonSlice';

const PayableCard = styled(Box)`
	background-color: #fff;
	border-radius: 8px;
	border-top: 2px solid #2f8ecb;
	margin-bottom: 16px;
	padding: 24px;
`;

const SubTitle = styled(Text)`
	font-size: 20px;
	font-weight: 400;
	line-height: 30px;
	margin-bottom: 16px;
	display: block;
`;

const PayableTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	margin-bottom: 16px;
`;

const PayableRow = styled.tr`
	border: 1px solid #dddddd;
`;

const PayableCell = styled.td`
	border: 1px solid #dddddd;
	text-align: left;
	padding: 12px;
	width: 50%;
`;

const LabelCell = styled(PayableCell)`
	font-weight: 400;
	background-color: ${(props) => (props.isTotal ? '#143e60' : '#fff')};
	color: ${(props) => (props.isTotal ? '#fff' : '#333')};
`;

const ValueCell = styled(PayableCell)`
	background-color: ${(props) => (props.isTotal ? '#143e60' : '#fff')};
	color: ${(props) => (props.isTotal ? '#fff' : '#333')};
	font-weight: ${(props) => (props.isTotal ? '600' : '400')};
`;

const HeaderRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
`;

const ActionButtons = styled.div`
	display: flex;
	gap: 8px;
	margin-top: 16px;
	justify-content: flex-end;
`;

const SaveButton = styled(Button)`
	background: #143e60;
	color: #ffffff;
	border: none;
	&:hover {
		background: #1a4d7a;
		color: #ffffff;
	}
`;

const CancelButton = styled(Button)`
	background: #fff;
	color: #333;
	border: 1px solid #d9d9d9;
	&:hover {
		border-color: #143e60;
		color: #143e60;
	}
`;

const EditButton = styled(Button)`
	background: #143e60;
	color: #ffffff;
	border: none;
	&:hover {
		background: #1a4d7a;
		color: #ffffff;
	}
`;

const TdsUploadContainer = styled.div`
	margin-top: 8px;
`;

const FileListWrapper = styled.div`
	margin-top: 8px;
`;
const toNumber = (value) => {
	if (value === null || value === undefined || value === '') return 0;
	if (typeof value === 'number') return value;
	const parsed = parseFloat(value);
	return Number.isNaN(parsed) ? 0 : parsed;
};

const pickRowDeduction = (obj) =>
	toNumber(
		obj?.deduction_amount ??
			obj?.vehicle_replacement_deduction_amount ??
			obj?.replacement_deduction_amount ??
			obj?.deducted_amount ??
			obj?.amount ??
			0
	);
const extractReplacementRows = (raw) => {
	if (Array.isArray(raw)) return raw;
	if (raw && typeof raw === 'object') {
		const nested = raw.vehicle_replacements || raw.replacements || raw.rows || raw.data;
		return Array.isArray(nested) ? nested : [raw];
	}
	return [];
};
const mapReplacementRow = (row) => {
	const isFlat = String(row.rate_basis || '').toLowerCase() === 'flat';
	const oldPrice = isFlat
		? row.old_vendor_rate ?? row.old_rate
		: row.old_charges_per_km ?? row.old_rate;
	const newPrice = isFlat
		? row.new_vendor_rate ?? row.new_rate
		: row.new_charges_per_km ?? row.new_rate;

	return {
		trip_id: row.trip_code ?? row.trip_id,
		rate_basis: row.rate_basis,
		old_vendor: row.old_vendor,
		new_vendor: row.new_vendor,
		old_vehicle_type: row.old_vehicle_type,
		new_vehicle_type: row.new_vehicle_type,
		old_driver: row.old_driver,
		new_driver: row.new_driver,
		initial: row.old_vehicle_number ?? row.initial,
		updated: row.new_vehicle_number ?? row.updated,
		old_price_value: oldPrice ?? row.old_price_value,
		new_price_value: newPrice ?? row.new_price_value,
		deduction_amount: pickRowDeduction(row),
		updated_by: row.updated_by,
		updated_at: row.replaced_at ?? row.updated_at,
		reason: row.reason,
	};
};

function EditableTotalPayable({
	vendorBillRes,
	onSave,
	onCancel,
	isEditable = true,
	useV2API = false,
	vendorBillId,
}) {
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const [isEditMode, setIsEditMode] = useState(false);
	const [loading, setLoading] = useState(false);
	// State to track files for each field (replacing single tdsFiles state)
	const [fieldFiles, setFieldFiles] = useState({});
	// State to track which fields the user has actually edited (for mandatory doc upload)
	const [changedFields, setChangedFields] = useState(new Set());
	// State for GST invoice upload modal
	const [gstInvoiceModalVisible, setGstInvoiceModalVisible] = useState(false);
	// State for GST invoice view modal
	const [gstInvoiceViewModalVisible, setGstInvoiceViewModalVisible] = useState(false);
	const [gstInvoiceViewUrl, setGstInvoiceViewUrl] = useState(null);
	const [gstInvoiceViewLoading, setGstInvoiceViewLoading] = useState(false);
	const [invoiceCleared, setInvoiceCleared] = useState(false);
	const [vehicleReplaceModalVisible, setVehicleReplaceModalVisible] = useState(false);

	// Fields that support document upload
	const FIELDS_WITH_UPLOAD = [
		'deduction_amount',
		'extra_hr_charges',
		'extra_km_charges',
		'labour_charges',
		'halt_charges',
		'miscellaneous_charges',
		'unloading_charges',
		'toll_amount',
	];

	const vendorBill = useMemo(
		() => vendorBillRes?.vendor_bill || {},
		[vendorBillRes?.vendor_bill]
	);

	// Extract bill_type from API response
	const billType = useMemo(
		() => vendorBill?.bill_type || vendorBillRes?.bill_type || null,
		[vendorBill?.bill_type, vendorBillRes?.bill_type]
	);

	// Extract has_invoice_payment from API response
	// If true, editing is disabled; if false or undefined, editing is allowed
	const hasInvoicePayment = useMemo(
		() => vendorBill?.has_invoice_payment || vendorBillRes?.has_invoice_payment || false,
		[vendorBill?.has_invoice_payment, vendorBillRes?.has_invoice_payment]
	);

	// Helper function to determine if a field is editable based on bill_type
	const isFieldEditable = useCallback(
		(fieldName) => {
			// GST amount is editable only when vendor is GST registered.
			if (fieldName === 'gst') {
				return vendorBillRes?.vendor_details?.is_gst_registered === true;
			}

			// If bill_type is not set, default to all fields editable (backward compatibility)
			if (!billType) {
				return true;
			}

			// For Intracity bills: All fields editable EXCEPT extra_km_charges and extra_hr_charges
			if (billType === 'intracity') {
				return fieldName !== 'extra_km_charges' && fieldName !== 'extra_hr_charges';
			}

			// For Intercity bills: Only TDS amount is editable
			if (billType === 'intercity') {
				return fieldName === 'tds_amount';
			}

			// Default: all fields editable for unknown bill types
			return true;
		},
		[billType]
	);

	// Helper to parse field value (handles strings like "0.00", "1100.36", etc.)
	const parseFieldValue = useCallback((value) => {
		if (value === null || value === undefined || value === '') return 0;
		if (typeof value === 'number') return value;
		if (typeof value === 'string') {
			const parsed = parseFloat(value);
			return Number.isNaN(parsed) ? 0 : parsed;
		}
		return 0;
	}, []);

	// Build initial form values from vendor bill
	const buildInitialValues = useCallback(() => {
		if (!vendorBill?.id) return null;

		return {
			total_payable_regular_trip_amount: parseFieldValue(
				vendorBill.total_payable_regular_trip_amount
			),
			total_payable_noload_trip_amount: parseFieldValue(
				vendorBill.total_payable_noload_trip_amount
			),
			total_payable_adhoc_trip_amount: parseFieldValue(
				vendorBill.total_payable_adhoc_trip_amount
			),
			bill_amount: parseFieldValue(vendorBill.bill_amount),
			advance_amount: parseFieldValue(vendorBillRes?.total_advance_amount),
			tds_amount: parseFieldValue(vendorBill.tds_amount),
			deduction_amount: parseFieldValue(vendorBill.deduction_amount),
			extra_km_charges: parseFieldValue(vendorBill.extra_km_charges),
			extra_hr_charges: parseFieldValue(vendorBill.extra_hr_charges),
			labour_charges: parseFieldValue(vendorBill.labour_charges),
			toll_amount: parseFieldValue(vendorBill.toll_amount),
			miscellaneous_charges: parseFieldValue(vendorBill.miscellaneous_charges),
			unloading_charges: parseFieldValue(vendorBill.unloading_charges),
			halt_charges: parseFieldValue(vendorBill.halt_charges),
			gst: parseFieldValue(vendorBill.gst),
		};
	}, [vendorBill, vendorBillRes?.total_advance_amount, parseFieldValue]);

	// Memoize initial values to avoid recalculating on every render
	const initialValues = useMemo(() => buildInitialValues(), [buildInitialValues]);

	// Debug: Log the data structure
	useEffect(() => {
		if (vendorBillRes) {
			console.log('EditableTotalPayable - vendorBillRes:', vendorBillRes);
			console.log('EditableTotalPayable - vendorBill:', vendorBill);
			console.log(
				'EditableTotalPayable - total_advance_amount:',
				vendorBillRes?.total_advance_amount
			);
			console.log('EditableTotalPayable - bill_amount:', vendorBill?.bill_amount);
		}
	}, [vendorBillRes, vendorBill]);

	// Helper function to get files for a specific field
	const getFieldFiles = useCallback((fieldName) => fieldFiles[fieldName] || [], [fieldFiles]);

	// Helper function to set files for a specific field
	const setFieldFilesForField = useCallback((fieldName, files) => {
		setFieldFiles((prev) => ({
			...prev,
			[fieldName]: files,
		}));
	}, []);

	// Initialize form values when data changes
	useEffect(() => {
		if (initialValues) {
			console.log('Setting form values:', initialValues);
			console.log('vendorBill data:', vendorBill);
			console.log('vendorBillRes data:', vendorBillRes);
			form.setFieldsValue(initialValues);
			console.log('Form after setFieldsValue:', form.getFieldsValue());

			// Initialize files for all fields from API response
			// Check for field-specific file arrays (e.g., tds_files, deduction_files, etc.)
			const fieldsWithFiles = [
				'tds_amount',
				'deduction_amount',
				'extra_hr_charges',
				'extra_km_charges',
				'labour_charges',
				'halt_charges',
				'miscellaneous_charges',
				'unloading_charges',
				'toll_amount',
			];

			const initialFieldFiles = {};
			fieldsWithFiles.forEach((fieldName) => {
				// Check for field-specific files (e.g., tds_files, deduction_files)
				const fileKey = `${fieldName.replace(/_/g, '_')}_files`;
				const files = vendorBill[fileKey] || vendorBill[`${fieldName}_files`];
				if (files && Array.isArray(files)) {
					initialFieldFiles[fieldName] = files;
				}
			});

			// Also check for legacy tds_files key
			if (vendorBill.tds_files && Array.isArray(vendorBill.tds_files)) {
				initialFieldFiles.tds_amount = vendorBill.tds_files;
			}

			if (Object.keys(initialFieldFiles).length > 0) {
				setFieldFiles(initialFieldFiles);
			}
		} else {
			console.log('No initial values - vendorBill?.id:', vendorBill?.id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, form, vendorBill]);

	// Exit edit mode if invoice payment has been made
	useEffect(() => {
		if (hasInvoicePayment && isEditMode) {
			setIsEditMode(false);
			form.resetFields();
			message.warning('Edit mode disabled: Invoice payment has been made');
		}
	}, [hasInvoicePayment, isEditMode, form]);

	// Helper to get field value with fallback priority: form > initialValues > vendorBill
	const getFieldValue = useCallback(
		(fieldName, fallbackValue = 0) => {
			const formValues = form.getFieldsValue();
			const hasFormValues = formValues && Object.keys(formValues).length > 0;

			if (hasFormValues && formValues[fieldName] !== undefined) {
				return parseFieldValue(formValues[fieldName]);
			}
			if (initialValues && initialValues[fieldName] !== undefined) {
				return parseFieldValue(initialValues[fieldName]);
			}
			if (fieldName === 'advance_amount') {
				return parseFieldValue(
					vendorBillRes?.total_advance_amount ||
						vendorBill.advance_amount ||
						fallbackValue
				);
			}
			return parseFieldValue(vendorBill[fieldName] || fallbackValue);
		},
		[form, initialValues, vendorBill, vendorBillRes, parseFieldValue]
	);

	// Helper to calculate base amount (same as getTotalAmount in old flow)
	const calculateBaseAmount = useCallback(() => {
		const totalAmount = getFieldValue('bill_amount');
		const advanceAmount = getFieldValue('advance_amount');
		const tdsAmount = getFieldValue('tds_amount');
		// Base = bill_amount - (tds_amount + advance_amount)
		return totalAmount - (tdsAmount + advanceAmount);
	}, [getFieldValue]);

	// Helper to calculate total payable with rounding (same as getTotal in old flow)
	const calculateTotalPayableWithRounding = useCallback(
		(baseAmount) => {
			const deductionAmount = getFieldValue('deduction_amount');
			const extraKmCharges = getFieldValue('extra_km_charges');
			const extraHrCharges = getFieldValue('extra_hr_charges');
			const labourCharges = getFieldValue('labour_charges');
			const tollAmount = getFieldValue('toll_amount');
			const miscellaneousCharges = getFieldValue('miscellaneous_charges');
			const unloadingCharges = getFieldValue('unloading_charges');
			const haltCharges = getFieldValue('halt_charges');

			// Total Payable = Base + extra_charges - deduction_amount
			const totalPayableAmount =
				baseAmount +
				extraKmCharges +
				extraHrCharges +
				labourCharges +
				tollAmount +
				miscellaneousCharges +
				unloadingCharges +
				haltCharges -
				deductionAmount;

			// Apply rounding logic (same as old flow)
			const intPart = Math.trunc(totalPayableAmount);
			const floatPart = Number((totalPayableAmount - intPart).toFixed(2));
			return floatPart <= 0.5
				? Math.floor(totalPayableAmount)
				: Math.ceil(totalPayableAmount);
		},
		[getFieldValue]
	);

	// Calculate total payable amount based on old flow formula
	// Formula: bill_amount - (tds_amount + advance_amount) + extra_charges - deduction_amount
	// This matches the old flow: getTotalAmount calculates base, then getTotal adds charges
	const calculateTotalPayable = useCallback(() => {
		// Step 1: Calculate base amount
		const baseAmount = calculateBaseAmount();

		// Step 2: Calculate total payable with rounding
		const roundedTotalPayable = calculateTotalPayableWithRounding(baseAmount);

		// Step 3: Add GST to get grand total
		const gst = getFieldValue('gst');
		const grandTotal = roundedTotalPayable + gst;

		return Math.max(0, grandTotal);
	}, [calculateBaseAmount, calculateTotalPayableWithRounding, getFieldValue]);

	// Helper to parse numeric value (handles string and number)
	const parseNumericValue = useCallback((value, defaultValue = 0) => {
		if (value === null || value === undefined || value === '') return defaultValue;
		if (typeof value === 'number') return value;
		if (typeof value === 'string') {
			const parsed = parseFloat(value);
			return Number.isNaN(parsed) ? defaultValue : parsed;
		}
		return defaultValue;
	}, []);

	// Use API values directly, or calculate in edit mode
	const totalPayableAmount = useMemo(() => {
		if (isEditMode) {
			// In edit mode, calculate from form values
			const baseAmount = calculateBaseAmount();
			return calculateTotalPayableWithRounding(baseAmount);
		}
		// In read-only mode, use API value
		return parseNumericValue(vendorBill?.total_payable_amount, 0);
	}, [
		isEditMode,
		vendorBill?.total_payable_amount,
		calculateBaseAmount,
		calculateTotalPayableWithRounding,
		parseNumericValue,
	]);

	const grandTotal = useMemo(() => {
		if (isEditMode) {
			// In edit mode, calculate from form values
			return calculateTotalPayable();
		}
		// In read-only mode, use API value
		return parseNumericValue(vendorBill?.grand_total, 0);
	}, [isEditMode, vendorBill?.grand_total, calculateTotalPayable, parseNumericValue]);

	const handleEdit = () => {
		// Prevent entering edit mode if invoice payment has been made
		if (hasInvoicePayment) {
			message.warning('Cannot edit charges after invoice payment has been made');
			return;
		}
		setChangedFields(new Set());
		setIsEditMode(true);
	};

	const handleCancel = () => {
		form.resetFields();
		setIsEditMode(false);
		setChangedFields(new Set());

		// Reset files to initial state from API
		const fieldsWithFiles = [
			'tds_amount',
			'deduction_amount',
			'extra_hr_charges',
			'extra_km_charges',
			'labour_charges',
			'halt_charges',
			'miscellaneous_charges',
			'unloading_charges',
			'toll_amount',
		];

		const resetFieldFiles = {};
		fieldsWithFiles.forEach((fieldName) => {
			const fileKey = `${fieldName.replace(/_/g, '_')}_files`;
			const files = vendorBill[fileKey] || vendorBill[`${fieldName}_files`];
			if (files && Array.isArray(files)) {
				resetFieldFiles[fieldName] = files;
			}
		});

		// Also check for legacy tds_files key
		if (vendorBill.tds_files && Array.isArray(vendorBill.tds_files)) {
			resetFieldFiles.tds_amount = vendorBill.tds_files;
		}

		setFieldFiles(resetFieldFiles);

		if (onCancel) {
			onCancel();
		}
	};

	// Handle form field value changes to track which fields the user has edited
	const handleValuesChange = useCallback(
		(changedValues) => {
			setChangedFields((prev) => {
				const next = new Set(prev);
				Object.keys(changedValues).forEach((fieldName) => {
					const currentValue = changedValues[fieldName];
					const initValue = initialValues?.[fieldName];
					// Mark as changed only if value actually differs from the initial value
					if (currentValue !== initValue) {
						next.add(fieldName);
					} else {
						next.delete(fieldName);
					}
				});
				return next;
			});
		},
		[initialValues]
	);

	// Helper function to check if a field has changed
	const hasFieldChanged = useCallback(
		(fieldName, currentValue, initialValue) => currentValue !== initialValue,
		[]
	);

	// Helper function to build payload for approval flow
	// Only includes fields that have changed from initial values and are editable based on bill_type
	const buildPayload = useCallback(
		(values) => {
			const payload = {
				id: vendorBillId || vendorBill.id,
			};

			// List of all charge fields
			const chargeFields = [
				'tds_amount',
				'gst',
				'deduction_amount',
				'extra_hr_charges',
				'extra_km_charges',
				'labour_charges',
				'halt_charges',
				'miscellaneous_charges',
				'unloading_charges',
				'toll_amount',
			];

			// Filter to only include editable fields based on bill_type
			const editableChargeFields = chargeFields.filter((fieldName) =>
				isFieldEditable(fieldName)
			);

			// Track which fields are being changed
			const changedChargeFields = [];

			// Add charge fields if they changed and are editable
			editableChargeFields.forEach((fieldName) => {
				const currentValue = values[fieldName];
				const initialValue = initialValues?.[fieldName];
				const hasChanged = hasFieldChanged(fieldName, currentValue, initialValue);

				if (!initialValues || hasChanged) {
					payload[fieldName] = currentValue || 0;
					changedChargeFields.push(fieldName);
				}
			});

			// Include image_keys for all fields that have files and are being changed
			// Build image_keys object with all fields that have files
			const imageKeys = {};
			changedChargeFields.forEach((fieldName) => {
				const files = getFieldFiles(fieldName);
				if (files && files.length > 0) {
					// Use the first file's filename (UUID-prefixed filename from get signed URL response)
					// The filename field contains the full S3 key that should be sent in image_keys payload
					const filename = files[0]?.filename || files[0]?.key || files[0]?.url;
					if (filename) {
						imageKeys[fieldName] = filename;
					}
				}
			});

			// Only include image_keys if there are any files
			if (Object.keys(imageKeys).length > 0) {
				payload.image_keys = imageKeys;
			}

			// Include remark if provided (optional)
			if (values.remark) {
				payload.remark = values.remark;
			}

			return payload;
		},
		[
			vendorBillId,
			vendorBill.id,
			getFieldFiles,
			initialValues,
			hasFieldChanged,
			isFieldEditable,
		]
	);

	// Helper function to check if payload has changes
	const hasPayloadChanges = useCallback((payload) => {
		// Check if any field values changed
		const hasFieldChanges = Object.keys(payload).some(
			(key) => key !== 'id' && key !== 'image_keys' && key !== 'remark'
		);

		// Check if any image_keys are present
		const hasImageKeys = payload.image_keys && Object.keys(payload.image_keys).length > 0;

		return hasFieldChanges || hasImageKeys;
	}, []);

	// Helper function to handle successful save response
	const handleSaveSuccess = useCallback(
		(response) => {
			const successMessage =
				response?.data?.message ||
				'Changes submitted for approval. They will be applied once accepted.';
			message.success(successMessage);
			setIsEditMode(false);
			if (onSave) {
				onSave();
			}
		},
		[onSave]
	);

	// Helper function to handle error response
	const handleSaveError = useCallback((response) => {
		const errorMessage =
			response?.error?.message ||
			response?.message ||
			'Failed to submit changes for approval';
		message.error(errorMessage);
	}, []);

	const handleSave = async () => {
		try {
			const values = await form.validateFields();

			// Validate: every changed field that supports document upload must have a file attached
			const missingDocFields = FIELDS_WITH_UPLOAD.filter((fieldName) => {
				if (!changedFields.has(fieldName)) return false; // Not edited – skip
				if (!isFieldEditable(fieldName)) return false; // Not editable for this bill type – skip
				const files = getFieldFiles(fieldName);
				return !files || files.length === 0;
			});

			if (missingDocFields.length > 0) {
				const fieldLabels = {
					tds_amount: 'TDS Amount',
					deduction_amount: 'Deduction Amount',
					extra_hr_charges: 'Extra Hr Charges',
					extra_km_charges: 'Extra Km Charges',
					labour_charges: 'Labour Charges',
					halt_charges: 'Halt Charges',
					miscellaneous_charges: 'Miscellaneous Charges',
					unloading_charges: 'Unloading Charges',
					toll_amount: 'Toll Amount',
				};
				const fieldNames = missingDocFields.map((f) => fieldLabels[f] || f).join(', ');
				message.error(`Please attach a supporting document for: ${fieldNames}`);
				return;
			}

			setLoading(true);

			const payload = buildPayload(values);

			// Check if there are any changes
			if (!hasPayloadChanges(payload)) {
				message.warning('No changes detected');
				setIsEditMode(false);
				return;
			}

			const action = updateVendorBillWithApproval({
				req_data: payload,
				useV2API,
			});

			const response = await dispatch(action);

			// Handle response based on status
			if (response?.status === 'success') {
				handleSaveSuccess(response);
			} else if (response?.status === 'error') {
				handleSaveError(response);
			} else {
				// Fallback success message
				handleSaveSuccess(response);
			}
		} catch (error) {
			console.error('Save error:', error);
			const errorMessage =
				error?.response?.data?.error?.message ||
				error?.message ||
				'Failed to submit changes for approval';
			message.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const formatCurrency = (value) =>
		`₹ ${(value || 0).toLocaleString('en-IN', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`;
	// Check if vendor is GST registered
	const isGstRegistered = useMemo(
		() => vendorBillRes?.vendor_details?.is_gst_registered === true,
		[vendorBillRes?.vendor_details?.is_gst_registered]
	);

	// non-GST vendors use non_gst_invoice_image
	const gstInvoiceImageKey = useMemo(
		() =>
			(isGstRegistered ? vendorBill?.gst_invoice_image : vendorBill?.non_gst_invoice_image) ||
			null,
		[isGstRegistered, vendorBill?.gst_invoice_image, vendorBill?.non_gst_invoice_image]
	);
	useEffect(() => {
		setInvoiceCleared(false);
	}, [gstInvoiceImageKey]);
	const effectiveInvoiceKey = invoiceCleared ? null : gstInvoiceImageKey;
	const invoiceNumber = useMemo(
		() => vendorBill?.vendor_invoice_number || null,
		[vendorBill?.vendor_invoice_number]
	);
	const invoiceDate = useMemo(
		() => vendorBill?.vendor_invoice_date || null,
		[vendorBill?.vendor_invoice_date]
	);
	const vehicleReplaceRaw = useMemo(
		() =>
			vendorBillRes?.vehicle_replacement_deductions ??
			vendorBillRes?.vehicleReplace ??
			vendorBillRes?.vehicle_replace ??
			vendorBillRes?.vehicle_replacement ??
			vendorBill?.vehicleReplace ??
			vendorBill?.vehicle_replace ??
			null,
		[vendorBillRes, vendorBill]
	);
	const vehicleReplaceData = useMemo(() => {
		if (!vehicleReplaceRaw) return null;

		const rawRows = extractReplacementRows(vehicleReplaceRaw);
		const mappedRows = rawRows.map(mapReplacementRow);
		const summedDeduction = rawRows.reduce((sum, row) => sum + pickRowDeduction(row), 0);
		const billDeduction = parseFieldValue(vendorBill?.vehicle_replacement_deduction_amount);
		const resolvedDeduction = billDeduction || summedDeduction;

		if (!mappedRows.length && !resolvedDeduction) return null;
		return { rows: mappedRows, deductionAmount: resolvedDeduction };
	}, [vehicleReplaceRaw, vendorBill, parseFieldValue]);

	// Handle GST invoice upload success
	const handleGstInvoiceUploadSuccess = useCallback(() => {
		// Refresh the data by calling onSave if available
		if (onSave) {
			onSave();
		}
	}, [onSave]);
	// Handle viewing GST invoice
	const handleViewGstInvoice = useCallback(async () => {
		if (!gstInvoiceImageKey) {
			message.error('GST invoice file not found');
			return;
		}

		setGstInvoiceViewModalVisible(true);
		setGstInvoiceViewLoading(true);
		setGstInvoiceViewUrl(null);

		try {
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'view',
					data: {
						entity: 'vendor_bill',
						keys: [gstInvoiceImageKey],
					},
				})
			);

			const signedUrl =
				(signedUrlResponse &&
					signedUrlResponse.status === 'success' &&
					signedUrlResponse.data?.[0]?.url) ||
				(signedUrlResponse?.data &&
					Array.isArray(signedUrlResponse.data) &&
					signedUrlResponse.data.length > 0 &&
					signedUrlResponse.data[0]?.url)
					? signedUrlResponse.data[0].url
					: null;

			if (signedUrl) {
				setGstInvoiceViewUrl(signedUrl);
			} else {
				message.error('Failed to load GST invoice');
				setGstInvoiceViewModalVisible(false);
			}
		} catch (error) {
			console.error('Error getting signed URL for GST invoice:', error);
			message.error('Failed to load GST invoice');
			setGstInvoiceViewModalVisible(false);
		} finally {
			setGstInvoiceViewLoading(false);
		}
	}, [dispatch, gstInvoiceImageKey]);

	// Render GST invoice view content
	const renderGstInvoiceViewContent = useCallback(() => {
		if (gstInvoiceViewLoading) {
			return (
				<div style={{ textAlign: 'center', padding: '40px' }}>
					<Spin size="large" tip="Loading GST invoice..." />
				</div>
			);
		}

		if (!gstInvoiceViewUrl) {
			return (
				<div style={{ textAlign: 'center', padding: '40px' }}>
					<p>Failed to load GST invoice</p>
					<Button onClick={handleViewGstInvoice}>Retry</Button>
				</div>
			);
		}

		const isPdf =
			gstInvoiceImageKey?.toLowerCase().endsWith('.pdf') ||
			gstInvoiceImageKey?.toLowerCase().includes('.pdf');

		if (isPdf) {
			return (
				<iframe
					src={gstInvoiceViewUrl}
					title="GST Invoice"
					style={{ width: '100%', height: '600px', border: 'none' }}
				/>
			);
		}

		return (
			<img
				src={gstInvoiceViewUrl}
				alt="GST Invoice"
				style={{
					width: '100%',
					height: 'auto',
					maxHeight: '70vh',
					objectFit: 'contain',
				}}
				onError={() => {
					message.error('Failed to load GST invoice image');
				}}
			/>
		);
	}, [gstInvoiceViewLoading, gstInvoiceViewUrl, gstInvoiceImageKey, handleViewGstInvoice]);

	// Extract filename from S3 key
	const extractFilename = (s3Key) => {
		if (!s3Key) return '';
		// Remove UUID prefix if present (format: uuid-filename.ext)
		const parts = s3Key.split('-');
		if (parts.length > 1) {
			// Check if first part looks like UUID (36 chars with dashes)
			const firstPart = parts[0];
			if (firstPart.length === 36 && firstPart.includes('-')) {
				// Remove UUID prefix
				return parts.slice(1).join('-');
			}
		}
		return s3Key;
	};

	// Helper function to render file upload for a field
	const renderFileUpload = (fieldName) => {
		const files = getFieldFiles(fieldName);
		const fieldIsEditable = isFieldEditable(fieldName);
		const isFieldChanged = changedFields.has(fieldName);
		const isMissingDoc = isFieldChanged && files.length === 0;

		if (isEditMode && fieldIsEditable) {
			return (
				<TdsUploadContainer>
					{isFieldChanged && (
						<div
							style={{
								fontSize: 11,
								marginBottom: 2,
								color: isMissingDoc ? '#ff4d4f' : '#52c41a',
								fontWeight: 500,
							}}
						>
							{isMissingDoc ? (
								<span style={{ color: '#ff4d4f' }}>
									* Document required for this field
								</span>
							) : (
								'✓ Document attached'
							)}
						</div>
					)}
					<TdsFileUpload
						files={files}
						onFilesChange={(newFiles) => setFieldFilesForField(fieldName, newFiles)}
						maxFiles={10}
						entity="vendor_bill"
						useV2API={useV2API}
					/>
					{isMissingDoc && (
						<div
							style={{
								border: '1px dashed #ff4d4f',
								bordeRadius: 4,
								padding: '4px 8px',
								marginTop: 4,
								fontSize: 11,
								color: '#ff4d4f',
								background: '#fff2f0',
							}}
						>
							Please attach a document to save the change.
						</div>
					)}
				</TdsUploadContainer>
			);
		}

		if (!isEditMode && files.length > 0) {
			return (
				<FileListWrapper>
					{files.map((file) => (
						<div
							key={file.id || file.key || file.name}
							style={{ marginTop: 4, fontSize: 12 }}
						>
							{file.name}
							{file.size ? ` (${(file.size / 1024 / 1024).toFixed(1)} MB)` : ''}
						</div>
					))}
				</FileListWrapper>
			);
		}

		return null;
	};

	const renderValue = (fieldName, isReadOnly = false, isEditableOverride = null) => {
		let fieldValue = form.getFieldValue(fieldName);

		// If form value is not set, try to get from memoized initial values
		if ((fieldValue === undefined || fieldValue === null) && initialValues) {
			fieldValue = initialValues[fieldName];
		}

		const displayValue =
			fieldValue !== undefined && fieldValue !== null ? parseFloat(fieldValue) || 0 : 0;

		// Determine if field should be editable
		// Priority: isReadOnly > isEditableOverride > isFieldEditable(fieldName)
		const shouldBeEditable =
			!isReadOnly &&
			(isEditableOverride !== null ? isEditableOverride : isFieldEditable(fieldName));

		if (isEditMode && shouldBeEditable) {
			return (
				<Form.Item name={fieldName} style={{ margin: 0 }}>
					<InputNumber
						style={{ width: '100%' }}
						prefix="₹"
						min={0}
						precision={2}
						formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
						parser={(val) => val.replace(/\$\s?|(,*)/g, '')}
					/>
				</Form.Item>
			);
		}
		return <span>{formatCurrency(displayValue)}</span>;
	};

	return (
		<PayableCard>
			<Form
				form={form}
				layout="vertical"
				initialValues={initialValues || {}}
				onValuesChange={handleValuesChange}
			>
				<HeaderRow>
					<SubTitle>TOTAL PAYABLE</SubTitle>
					{!isEditMode && isEditable && !hasInvoicePayment && (
						<EditButton icon={<EditOutlined />} onClick={handleEdit}>
							Edit Charges
						</EditButton>
					)}
				</HeaderRow>

				<PayableTable>
					<tbody>
						<PayableRow>
							<LabelCell isLabel>Total Payable Regular Trip Payment:</LabelCell>
							<ValueCell>
								{renderValue('total_payable_regular_trip_amount', true)}
							</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>No Load Trip Payment:</LabelCell>
							<ValueCell>
								{renderValue('total_payable_noload_trip_amount', true)}
							</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>Adhoc Trip Payment:</LabelCell>
							<ValueCell>
								{renderValue('total_payable_adhoc_trip_amount', true)}
							</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel style={{ fontWeight: 600 }}>
								Total Amount:
							</LabelCell>
							<ValueCell style={{ fontWeight: 600 }}>
								{renderValue('bill_amount', true)}
							</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>Advance Amount:</LabelCell>
							<ValueCell>{renderValue('advance_amount', true)}</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>TDS Amount (Deducted):</LabelCell>
							<ValueCell>{renderValue('tds_amount')}</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>
								Deduction Amount (Deducted):
								{renderFileUpload('deduction_amount')}
							</LabelCell>
							<ValueCell>{renderValue('deduction_amount')}</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>
								<span
									style={{
										display: 'inline-flex',
										alignItems: 'center',
										gap: 8,
									}}
								>
									Vehicle Replacement Deduction Amount:
									<EyeOutlined
										style={{
											fontSize: 14,
											color: '#1890ff',
											cursor: 'pointer',
										}}
										onClick={() => setVehicleReplaceModalVisible(true)}
										title="View Replacement History"
									/>
								</span>
							</LabelCell>
							<ValueCell>
								{formatCurrency(vehicleReplaceData?.deductionAmount ?? 0)}
							</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>
								Extra Km Charges:
								{renderFileUpload('extra_km_charges')}
							</LabelCell>
							<ValueCell>{renderValue('extra_km_charges')}</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>
								Extra Hr Charges:
								{renderFileUpload('extra_hr_charges')}
							</LabelCell>
							<ValueCell>{renderValue('extra_hr_charges')}</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>
								Labour Charges:
								{renderFileUpload('labour_charges')}
							</LabelCell>
							<ValueCell>{renderValue('labour_charges')}</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>
								Toll Amount:
								{renderFileUpload('toll_amount')}
							</LabelCell>
							<ValueCell>{renderValue('toll_amount')}</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>
								Miscellaneous Charges:
								{renderFileUpload('miscellaneous_charges')}
							</LabelCell>
							<ValueCell>{renderValue('miscellaneous_charges')}</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>
								Unloading Charges:
								{renderFileUpload('unloading_charges')}
							</LabelCell>
							<ValueCell>{renderValue('unloading_charges')}</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>
								Halt Charges:
								{renderFileUpload('halt_charges')}
							</LabelCell>
							<ValueCell>{renderValue('halt_charges')}</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel>GST (18%):</LabelCell>
							<ValueCell>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: 10,
									}}
								>
									<span>{renderValue('gst')}</span>
									{!isEditMode && (
										<>
											<div
												style={{
													display: 'flex',
													alignItems: 'stretch',
													gap: 8,
													flexWrap: 'wrap',
												}}
											>
												<Button
													size="small"
													icon={<PaperClipOutlined />}
													onClick={() => setGstInvoiceModalVisible(true)}
													style={{
														height: 'auto',
														display: 'inline-flex',
														alignItems: 'center',
													}}
												>
													{gstInvoiceImageKey
														? 'Re-attach GST Invoice'
														: 'Attach GST Invoice'}
												</Button>
												{/* Invoice number/date are read-only here — editable only
												    from inside the Attach GST Invoice modal. */}
												<Input
													size="small"
													disabled
													placeholder="Enter Invoice Number"
													value={invoiceNumber || ''}
													style={{ width: 150 }}
												/>
												<DatePicker
													size="small"
													disabled
													placeholder="Select Invoice Date"
													format="DD-MM-YYYY"
													value={invoiceDate ? moment(invoiceDate) : null}
													style={{ width: 150 }}
												/>
											</div>
											{effectiveInvoiceKey && (
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: 6,
													}}
												>
													<FileTextOutlined style={{ color: '#333' }} />
													<span
														style={{
															fontSize: 12,
															color: '#1890ff',
															cursor: 'pointer',
															textDecoration: 'underline',
														}}
														onClick={handleViewGstInvoice}
														onKeyDown={(e) => {
															if (
																e.key === 'Enter' ||
																e.key === ' '
															) {
																e.preventDefault();
																handleViewGstInvoice();
															}
														}}
														role="button"
														tabIndex={0}
														title="View GST Invoice"
													>
														Attach GST Invoice
													</span>
												</div>
											)}
										</>
									)}
								</div>
							</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel style={{ fontWeight: 600 }}>
								Total Payable Amount:
							</LabelCell>
							<ValueCell style={{ fontWeight: 600 }}>
								{formatCurrency(totalPayableAmount)}
							</ValueCell>
						</PayableRow>
						<PayableRow>
							<LabelCell isLabel isTotal style={{ fontWeight: 600 }}>
								Grand Total:
							</LabelCell>
							<ValueCell isTotal style={{ fontWeight: 600 }}>
								{formatCurrency(grandTotal)}
							</ValueCell>
						</PayableRow>
					</tbody>
				</PayableTable>

				{isEditMode && (
					<ActionButtons>
						<CancelButton icon={<CloseOutlined />} onClick={handleCancel}>
							Cancel
						</CancelButton>
						<SaveButton icon={<SaveOutlined />} onClick={handleSave} loading={loading}>
							Save
						</SaveButton>
					</ActionButtons>
				)}
			</Form>
			<VehicleReplacementHistoryModal
				visible={vehicleReplaceModalVisible}
				vehicleReplacements={vehicleReplaceData?.rows || []}
				onClose={() => setVehicleReplaceModalVisible(false)}
			/>

			{/* GST Invoice Upload Modal */}
			<GstInvoiceUploadModal
				visible={gstInvoiceModalVisible}
				onClose={() => setGstInvoiceModalVisible(false)}
				vendorBillId={vendorBillId || vendorBill.id}
				useV2API={useV2API}
				onSuccess={handleGstInvoiceUploadSuccess}
				isGstRegistered={isGstRegistered}
				initialValues={{
					invoice_number: invoiceNumber,
					invoice_date: invoiceDate,
					image_key: effectiveInvoiceKey,
				}}
			/>

			{/* GST Invoice View Modal */}
			<Modal
				title={`GST Invoice - ${
					gstInvoiceImageKey ? extractFilename(gstInvoiceImageKey) : ''
				}`}
				visible={gstInvoiceViewModalVisible}
				onCancel={() => {
					setGstInvoiceViewModalVisible(false);
					setGstInvoiceViewUrl(null);
				}}
				footer={[
					<Button
						key="close"
						onClick={() => {
							setGstInvoiceViewModalVisible(false);
							setGstInvoiceViewUrl(null);
						}}
					>
						Close
					</Button>,
				]}
				width={800}
				centered
			>
				{renderGstInvoiceViewContent()}
			</Modal>
		</PayableCard>
	);
}

EditableTotalPayable.propTypes = {
	vendorBillRes: PropTypes.object,
	onSave: PropTypes.func,
	onCancel: PropTypes.func,
	isEditable: PropTypes.bool,
	useV2API: PropTypes.bool,
	vendorBillId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default EditableTotalPayable;
